import { hexColorRegEx } from '@logto/core-kit';
import { languageTagGuard } from '@logto/language-kit';
import { z } from 'zod';
import { Theme } from '../../types/theme.js';
export const colorGuard = z.object({
    primaryColor: z.string().regex(hexColorRegEx),
    isDarkModeEnabled: z.boolean(),
    darkPrimaryColor: z.string().regex(hexColorRegEx),
});
export const partialColorGuard = colorGuard.partial();
/** Maps a theme to the key of the logo URL in the {@link Branding} object. */
export const themeToLogoKey = Object.freeze({
    [Theme.Light]: 'logoUrl',
    [Theme.Dark]: 'darkLogoUrl',
});
export const brandingGuard = z
    .object({
    logoUrl: z.string().url(),
    darkLogoUrl: z.string().url(),
    favicon: z.string().url(),
    darkFavicon: z.string().url(),
})
    .partial();
export const languageInfoGuard = z.object({
    autoDetect: z.boolean(),
    fallbackLanguage: languageTagGuard,
});
export var SignInIdentifier;
(function (SignInIdentifier) {
    SignInIdentifier["Username"] = "username";
    SignInIdentifier["Email"] = "email";
    SignInIdentifier["Phone"] = "phone";
})(SignInIdentifier || (SignInIdentifier = {}));
export const signInIdentifierGuard = z.nativeEnum(SignInIdentifier);
export var AdditionalIdentifier;
(function (AdditionalIdentifier) {
    AdditionalIdentifier["UserId"] = "userId";
})(AdditionalIdentifier || (AdditionalIdentifier = {}));
export const signUpGuard = z.object({
    identifiers: z.nativeEnum(SignInIdentifier).array(),
    password: z.boolean(),
    verify: z.boolean(),
});
export const signInGuard = z.object({
    methods: z
        .object({
        identifier: z.nativeEnum(SignInIdentifier),
        password: z.boolean(),
        verificationCode: z.boolean(),
        isPasswordPrimary: z.boolean(),
    })
        .array(),
});
export const socialSignInGuard = z.object({
    automaticAccountLinking: z.boolean().optional(),
});
export const connectorTargetsGuard = z.string().array();
export const customContentGuard = z.record(z.string());
export var MfaFactor;
(function (MfaFactor) {
    MfaFactor["TOTP"] = "Totp";
    MfaFactor["WebAuthn"] = "WebAuthn";
    MfaFactor["BackupCode"] = "BackupCode";
    MfaFactor["Email"] = "Email";
    MfaFactor["SMS"] = "SMS";
    MfaFactor["WA"] = "Whatsapp";
})(MfaFactor || (MfaFactor = {}));
export const mfaFactorsGuard = z.nativeEnum(MfaFactor).array();
export var MfaPolicy;
(function (MfaPolicy) {
    /** @deprecated, use `PromptAtSignInAndSignUp` instead */
    MfaPolicy["UserControlled"] = "UserControlled";
    /** MFA is required for all users */
    MfaPolicy["Mandatory"] = "Mandatory";
    /** Ask users to set up MFA on their sign-in after registration (skippable, one-time prompt) */
    MfaPolicy["PromptOnlyAtSignIn"] = "PromptOnlyAtSignIn";
    /** Ask users to set up MFA during registration (skippable, one-time prompt) */
    MfaPolicy["PromptAtSignInAndSignUp"] = "PromptAtSignInAndSignUp";
    /** Do not ask users to set up MFA */
    MfaPolicy["NoPrompt"] = "NoPrompt";
})(MfaPolicy || (MfaPolicy = {}));
/**
 * TMMIN MFA — Trusted Device (remember-device) configuration.
 * When `enabled` is true, the experience UI surfaces a "Remember this
 * device for 30 days" checkbox and the backend accepts trusted-device
 * cookie issuance/verification.
 */
export const trustedDeviceConfigGuard = z.object({
    enabled: z.boolean(),
    /** Days a trusted device stays valid. Defaults to 30 when absent. */
    ttlDays: z.number().int().min(1).max(365).optional(),
});
/**
 * TMMIN MFA — sentinel (failed-attempt lockout) configuration. Absent fields
 * fall back to the built-in defaults (5 attempts / 1 hour window, 10-minute block).
 */
export const sentinelPolicyGuard = z.object({
    maxAttempts: z.number().int().min(1).max(100).optional(),
    blockDurationMinutes: z.number().int().min(1).max(1440).optional(),
});
/**
 * TMMIN MFA — WhatsApp Reverse-OTP configuration.
 * Holds provider credentials and message settings. This config is stored
 * in the sign-in-experience `mfa` jsonb column but is OMITTED from the
 * public `/.well-known/sign-in-exp` payload so secrets never reach the browser.
 */
export const whatsAppReverseOtpConfigGuard = z.object({
    enabled: z.boolean(),
    provider: z.enum(['meta', 'openwa']),
    businessNumber: z.string(),
    messageTemplate: z.string(),
    meta: z
        .object({ phoneNumberId: z.string(), appSecret: z.string(), verifyToken: z.string() })
        .partial()
        .optional(),
    openwa: z
        .object({
        baseUrl: z.string(),
        apiKey: z.string(),
        sessionId: z.string(),
        webhookSecret: z.string(),
        signatureHeader: z.string(),
    })
        .partial()
        .optional(),
});
export const mfaGuard = z.object({
    factors: mfaFactorsGuard,
    policy: z.nativeEnum(MfaPolicy),
    trustedDevice: trustedDeviceConfigGuard.optional(),
    whatsappReverseOtp: whatsAppReverseOtpConfigGuard.optional(),
    /** Max wrong-code attempts per verification code. Defaults to 10 when absent. */
    otpMaxTryCount: z.number().int().min(1).max(100).optional(),
    /** OIDC interaction (sign-in session) TTL in seconds. Defaults to 3600 when absent. */
    interactionTtlSeconds: z
        .number()
        .int()
        .min(300)
        .max(24 * 60 * 60)
        .optional(),
    /**
     * OIDC Session TTL in days — how long an authenticated session (SSO) stays valid before the user
     * must sign in again. Drives node-oidc-provider `ttl.Session`. Defaults to 7 days when absent.
     */
    sessionTtlDays: z.number().int().min(1).max(30).optional(),
    /**
     * Admin console idle-logout timeout in minutes. When set, the console signs the admin out after
     * this many minutes of inactivity. Client-side only — does not affect the OIDC Session TTL.
     * Absent = disabled (no idle auto-logout).
     */
    idleTimeoutMinutes: z.number().int().min(1).max(120).optional(),
    sentinelPolicy: sentinelPolicyGuard.optional(),
});
export const customUiAssetsGuard = z.object({
    id: z.string(),
    createdAt: z.number(),
});
